import {graphql} from 'gatsby'
import ky from 'ky'
import React from 'react'
import styled from 'styled-components'

import {
    contactUsPageContactFormActioned,
    ContactUsPageContactFormActionedAction,
    UserStatus,
} from '@/analytics/segment'
import Button from '@/components/button'
import {DynamicForm, FieldType, FieldWidth} from '@/components/dynamic-form'
import FeatureBlock from '@/components/feature-block'
import Image from '@/components/image'
import {useUser} from '@/hooks/use-user'
import {useGlobalStrings} from '@/providers/global-strings'
import {Layout, Section, BackgroundWrapper} from '@/styles/grid'
import {tokens} from '@/theme'

import type {Team} from '@/analytics/segment'
import type {FieldProps, State} from '@/components/dynamic-form'
import type {Props as RichTextProps} from '@/components/rich-text'
import type {ImageSource} from '@/types/image'

const RECIPIENTS = {
    SUPPORT: 'support',
} as const

const EMAILS = {
    support: 'support@pleo.io',
} as const

export interface Props {
    title: string
    image: ImageSource
    successTitle: string
    successBody: RichTextProps['content']
    successCta: string
    successImage: ImageSource
    privacyDisclaimer: RichTextProps['content']
}

export const ContactForm = ({
    title,
    image,
    successTitle,
    successBody,
    successCta,
    successImage,
    privacyDisclaimer,
}: Props) => {
    const [isSubmitted, setIsSubmitted] = React.useState(false)
    const globalStrings = useGlobalStrings()
    const {isLoggedIn} = useUser()

    const fields: FieldProps[] = [
        {
            key: 'firstName',
            type: FieldType.Text,
            label: globalStrings.first_name,
            validators: ['isRequired'],
            errorMessage: globalStrings.first_name_error,
            width: FieldWidth.Half,
        },
        {
            key: 'lastName',
            type: FieldType.Text,
            label: globalStrings.last_name,
            validators: ['isRequired'],
            errorMessage: globalStrings.last_name_error,
            width: FieldWidth.Half,
        },
        {
            key: 'email',
            type: FieldType.Email,
            label: globalStrings.work_email,
            validators: ['isRequired', 'isValidEmail'],
            errorMessage: globalStrings.non_work_email_error,
            width: FieldWidth.Half,
        },
        {
            key: 'companyName',
            type: FieldType.Text,
            label: globalStrings.company_name,
            validators: ['isRequired'],
            errorMessage: globalStrings.this_field_is_required,
            width: FieldWidth.Half,
        },
        {
            key: 'message',
            type: FieldType.Textarea,
            label: globalStrings.your_message,
            validators: ['isRequired'],
            errorMessage: globalStrings.this_field_is_required,
            width: FieldWidth.Full,
        },
    ]

    const handleSubmit = async (state: State) => {
        const payload = {
            ...state,
            sendTo: EMAILS[RECIPIENTS.SUPPORT],
            type: RECIPIENTS.SUPPORT,
        }

        await ky
            .post('/api/contact-form-submit', {
                json: payload,
            })
            .then(() => {
                contactUsPageContactFormActioned({
                    action: ContactUsPageContactFormActionedAction.Submitted,
                    team: RECIPIENTS.SUPPORT as Team,
                    user_status: isLoggedIn ? UserStatus.LoggedIn : UserStatus.NotLoggedIn,
                })
                setIsSubmitted(true)
            })
    }

    return (
        <BackgroundWrapper $color="default">
            <StyledSection>
                {!isSubmitted ? (
                    <Layout $variant="two-asymmetrical">
                        <DynamicForm
                            titleAs="h3"
                            title={title}
                            fields={fields}
                            handleSubmit={handleSubmit}
                            submitLabel={globalStrings.send_message}
                            privacyDisclaimer={privacyDisclaimer}
                            captchaAction="contact_form"
                        />
                        <Image image={image} width={600} />
                    </Layout>
                ) : (
                    <FeatureBlock image={successImage} title={successTitle} body={successBody}>
                        <Button $variant="outline" onClick={() => setIsSubmitted(false)}>
                            {successCta}
                        </Button>
                    </FeatureBlock>
                )}
            </StyledSection>
        </BackgroundWrapper>
    )
}

const StyledSection = styled(Section)`
    display: grid;
    gap: ${tokens.spacing60};
`

export const contactFormSliceFragment = graphql`
    fragment ContactFormSlice on PrismicSlicePageDataBodyContactForm {
        __typename
        primary {
            title
            success_title
            success_cta
            success_body_support {
                richText
            }
            privacy_disclaimer {
                richText
            }
            image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
            success_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
                gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $twoColumnsAsymmetric)
            }
        }
    }
`
